import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RewardApprovalBehavior, RewardCalculationType, RewardType } from '@/api/model'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { HelpHint } from '@/components/common/help/HelpHint'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import { WalletTypeSelect } from '@/components/common/WalletTypeSelect'
import type { EditorActionModule, RewardCalculationState } from '@/lib/action-modules/registry/types'
import { rewardCalculationTypes } from '@/lib/action-modules/registry'
import { helpKey } from '@/lib/action-modules/help'

interface Props {
  value: RewardCalculationState
  onChange: (value: RewardCalculationState) => void
  actionModule: EditorActionModule
  onModuleChanged: (patch: Partial<EditorActionModule>) => void
  /** Set in admin scope so the wallet type list is read for the edited company. */
  walletTypeCompanyId?: number
  disabled: boolean
}

/** The backend stores the delay as a .NET TimeSpan ("d.hh:mm:ss"), but we only expose
 *  whole-day precision. Convert between a day count and a whole-day TimeSpan string. */
function timeSpanToDays(ts: string | null): number | null {
  if (!ts) return null
  const match = /^(?:(\d+)\.)?\d{1,2}:\d{2}:\d{2}/.exec(ts.trim())
  return match ? Number(match[1] ?? 0) : null
}
function daysToTimeSpan(days: number | null): string | null {
  return days === null ? null : `${days}.00:00:00`
}

const approvalBehaviorOrder = [
  RewardApprovalBehavior.BySystem,
  RewardApprovalBehavior.ByEmployee,
  RewardApprovalBehavior.DeclineByEmployee,
  // ByCompanyManagementUser and DeclineByCompanyManagementUser is not implemented as of now
  //RewardApprovalBehavior.ByCompanyManagementUser,
  //RewardApprovalBehavior.DeclineByCompanyManagementUser,
]

export default function RewardCalculationTab({
  value,
  onChange,
  actionModule,
  onModuleChanged,
  walletTypeCompanyId,
  disabled,
}: Props) {
  const { t } = useTranslation('action-modules-wizard')
  // The transaction delay takes precedence over the day-of-month in the backend completion
  // logic — when a delay is set, the day-of-month field is disabled to make that visible.
  const delayActive = (actionModule.transactionTimestampDelay ?? '').trim() !== ''
  const definition = useMemo(
    () => rewardCalculationTypes.find(i => i.type === value.selectedRewardCalculationType),
    [value.selectedRewardCalculationType],
  )

  // The wallet type is persisted on the reward calculation, so it can only be saved once one
  // exists. Disabled rather than hidden, so the field does not appear and vanish in the row.
  const walletTypeDisabled = value.selectedRewardCalculationType === null || disabled

  const onChangeType = (newType: RewardCalculationType) => {
    const def = rewardCalculationTypes.find(i => i.type === newType)
    onChange({
      ...value,
      selectedRewardCalculationType: newType,
      rewardCalculationType: { ...(def?.defaultValue ?? {}), type: newType },
    })
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <fieldset disabled={disabled}>
        <h3 className="text-sm font-semibold">{t('tabReward.common')}</h3>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardType')}>{t('tabReward.rewardType')}</HelpLabel>
            <Select value={value.rewardType} onValueChange={v => onChange({...value, rewardType: v as RewardType})} disabled={disabled}>
              <SelectTrigger className="w-full">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={RewardType.Nothing}>{t('tabReward.rewardTypeNothing')}</SelectItem>
                <SelectItem value={RewardType.Flames}>{t('tabReward.rewardTypeFlames')}</SelectItem>
                <SelectItem value={RewardType.Points}>{t('tabReward.rewardTypePoints')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardMaxReward')}>{t('tabReward.maxReward')}</HelpLabel>
            <Input
              type="number"
              value={value.maxReward}
              onChange={e => onChange({...value, maxReward: Number(e.target.value)})}
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardWalletType')}>{t('tabReward.walletType')}</HelpLabel>
            <WalletTypeSelect
              value={value.walletTypeId}
              onChange={walletTypeId => onChange({...value, walletTypeId})}
              disabled={walletTypeDisabled}
              companyId={walletTypeCompanyId}
            />
            {walletTypeDisabled && (
              <p className="text-xs text-muted-foreground">{t('tabReward.walletTypeNeedsCalculationType')}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardApprovalBehavior')}>{t('tabReward.rewardApprovalBehavior')}</HelpLabel>
            <Select
              disabled={disabled}
              value={actionModule.rewardApprovalBehavior}
              onValueChange={v => onModuleChanged({rewardApprovalBehavior: v as RewardApprovalBehavior})}
            >
              <SelectTrigger className="w-full">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent>
                {approvalBehaviorOrder.map(b => (
                  <SelectItem key={b} value={b}>
                    {t(`tabReward.rewardApprovalBehaviorOptions.${b}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardTransactionTimestampDelay')}>
              {t('tabReward.transactionTimestampDelay')}
            </HelpLabel>
            <Input
              type="number"
              min={0}
              step={1}
              value={timeSpanToDays(actionModule.transactionTimestampDelay) ?? ''}
              placeholder={t('tabReward.transactionTimestampDelayPlaceholder')}
              onChange={e =>
                onModuleChanged({
                  transactionTimestampDelay:
                    e.target.value !== '' ? daysToTimeSpan(Math.max(0, Math.floor(Number(e.target.value)))) : null,
                })
              }
            />
          </div>
          <div className="flex flex-1 flex-col gap-2">
            <HelpLabel helpKey={helpKey('rewardTransactionTimestampDayOfMonth')}>
              {t('tabReward.transactionTimestampDayOfMonth')}
            </HelpLabel>
            <Input
              type="number"
              min={1}
              max={31}
              disabled={delayActive}
              value={actionModule.transactionTimestampDayOfMonth ?? ''}
              placeholder={t('tabReward.transactionTimestampDayOfMonthPlaceholder')}
              onChange={e =>
                onModuleChanged({transactionTimestampDayOfMonth: e.target.value !== '' ? Number(e.target.value) : null})
              }
            />
          </div>
        </div>
        <HelpText helpKey={helpKey('rewardTransactionTimestamp')}/>

        <div className="flex items-center gap-1.5 pt-2">
          <h3 className="text-sm font-semibold">{t('tabReward.rewardCalculationType')}</h3>
          <HelpHint helpKey={helpKey('rewardCalculationType')}/>
        </div>
        <div className="flex max-w-md flex-col gap-2">
          <Select
            disabled={disabled}
            value={value.selectedRewardCalculationType ?? undefined}
            onValueChange={v => onChangeType(v as RewardCalculationType)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('tabReward.rewardCalculationType')}/>
            </SelectTrigger>
            <SelectContent>
              {rewardCalculationTypes.map(s => (
                <SelectItem key={s.type} value={s.type}>
                  {t(s.nameKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {definition?.helpKey && <HelpText helpKey={definition.helpKey}/>}
        </div>

        {definition && value.rewardType !== RewardType.Nothing && (
          <div className="pt-1">
            {definition.editor({
              value: value.rewardCalculationType,
              onChanged: v => onChange({...value, rewardCalculationType: v}),
            })}
          </div>
        )}
      </fieldset>
    </div>
  )
}
